import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Globe } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../i18n';

const LANGS = [
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

export const LanguageSwitcher = ({ variant = 'desktop' }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const currentLang = LANGS.find((l) => l.code === i18n.language) || LANGS[1];

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setOpen(false);

    // Update URL prefix to reflect language for SEO
    const segments = location.pathname.split('/').filter(Boolean);
    const first = segments[0];
    let rest = segments;
    if (SUPPORTED_LANGUAGES.includes(first)) {
      rest = segments.slice(1);
    }
    const newPath = '/' + [lang, ...rest].join('/');
    navigate(newPath + location.hash, { replace: true });
  };

  if (variant === 'flags') {
    // Desktop compact flag bar
    return (
      <div className="flex items-center space-x-1" data-testid="language-switcher-flags">
        {LANGS.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => changeLanguage(l.code)}
            className={`text-xl leading-none transition-all duration-200 hover:scale-125 ${
              i18n.language === l.code ? 'opacity-100 scale-110' : 'opacity-50 hover:opacity-100'
            }`}
            aria-label={`Change language to ${l.label}`}
            data-testid={`lang-flag-${l.code}`}
            title={l.label}
          >
            {l.flag}
          </button>
        ))}
      </div>
    );
  }

  // Dropdown variant (default)
  return (
    <div ref={ref} className="relative" data-testid="language-switcher-dropdown">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 hover:border-amber-500 bg-white transition-colors"
        aria-label="Change language"
        data-testid="lang-dropdown-trigger"
      >
        <Globe className="w-4 h-4 text-gray-700" />
        <span className="text-lg leading-none">{currentLang.flag}</span>
        <span className="text-sm font-medium text-gray-700 uppercase">{currentLang.code}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => changeLanguage(l.code)}
              className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-amber-50 transition-colors ${
                i18n.language === l.code ? 'bg-amber-50 text-amber-700 font-semibold' : 'text-gray-700'
              }`}
              data-testid={`lang-option-${l.code}`}
            >
              <span className="text-xl">{l.flag}</span>
              <span className="text-sm">{l.label}</span>
              {i18n.language === l.code && <span className="ml-auto text-amber-600">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
